import type { DashboardLayoutDto, DashboardSection } from '@ecom/contracts';

/**
 * How the overview lays out each person's sections (ADR-109).
 *
 * A section is the whole width — the figures, the recent orders table — or
 * one side of a row: two side sections in a row share it, the first one's
 * share larger when it is the wider kind; one left alone takes the row.
 */
export const SECTION_WIDTH: Record<DashboardSection, 'full' | 'wide' | 'narrow'> = {
  metrics: 'full',
  salesTrend: 'wide',
  topSellers: 'narrow',
  actionQueue: 'wide',
  lowStock: 'narrow',
  customersToCall: 'wide',
  recentOrders: 'full',
  visits: 'narrow',
  myFollowUps: 'narrow',
  insights: 'wide',
  target: 'narrow',
  live: 'full',
};

/** What each section reads beyond the overview itself — without it, it is neither shown nor offered. */
export const SECTION_NEEDS: Partial<Record<DashboardSection, string>> = {
  actionQueue: 'orders:read',
  recentOrders: 'orders:read',
  lowStock: 'inventory:manage',
  customersToCall: 'customers:read',
  myFollowUps: 'customers:read',
  // The latest orders, by number and city.
  live: 'orders:read',
};

/** The sections shown, in order, grouped into the rows they fill. */
export function rowsOf(layout: DashboardLayoutDto, may: (permission: string) => boolean): DashboardSection[][] {
  const shown = layout.sections
    .filter((section) => !section.hidden && (!SECTION_NEEDS[section.id] || may(SECTION_NEEDS[section.id]!)))
    .map((section) => section.id);
  const rows: DashboardSection[][] = [];
  for (let index = 0; index < shown.length; index += 1) {
    const id = shown[index]!;
    const next = shown[index + 1];
    if (SECTION_WIDTH[id] !== 'full' && next && SECTION_WIDTH[next] !== 'full') {
      rows.push([id, next]);
      index += 1;
    } else {
      rows.push([id]);
    }
  }
  return rows;
}

/** A shared row's columns on a wide screen: the wide kind gets more room beside a narrow one. */
export function columnsOf(row: DashboardSection[]): string | undefined {
  if (row.length < 2) return undefined;
  const share = (id: DashboardSection) => (SECTION_WIDTH[id] === 'wide' ? 1.5 : 1);
  const [first, second] = row as [DashboardSection, DashboardSection];
  return share(first) === share(second) ? '1fr 1fr' : `${share(first)}fr ${share(second)}fr`;
}

/**
 * The full layout after the sections on offer were reordered: the ones a
 * person may not see keep their places, so saving never moves what they
 * could not touch.
 */
export function withOffered(layout: DashboardLayoutDto, offered: DashboardLayoutDto['sections']): DashboardLayoutDto {
  const ids = new Set(offered.map((section) => section.id));
  const queue = [...offered];
  return { sections: layout.sections.map((section) => (ids.has(section.id) ? queue.shift()! : section)) };
}
